import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { roleConfig } from './config/rolePermissions';
import Layout from './components/Layout/Layout';
import TeacherLayout from './components/Layout/TeacherLayout';
import MentorProtectedRoute from './components/ProtectedRoute/MentorProtectedRoute';
import TeacherProtectedRoute from './components/ProtectedRoute/TeacherProtectedRoute';
import ComingSoon from './components/ComingSoon/ComingSoon';
import Dashboard from './pages/Dashboard/Dashboard';
import Courses from './pages/Courses/Courses';
import CourseCreation from './pages/CourseCreation/CourseCreation';
import CourseOverview from './pages/CourseOverview/CourseOverview';
import Login from './pages/Login/Login';
import Register from './pages/Register/Register';
import ForgotPassword from './pages/ForgotPassword/ForgotPassword';
import StudentsHub from './pages/StudentsHub/StudentsHub';
import SalesHub from './pages/SalesHub/SalesHub';
import InboxHub from './pages/InboxHub/InboxHub';
import SupportHub from './pages/SupportHub/SupportHub';
import MentorProfile from './pages/MentorProfile/MentorProfile';
import MentorDirectory from './pages/MentorDirectory/MentorDirectory';
import CourseEnrollments from './pages/CourseEnrollments/CourseEnrollments';
import CourseRevenue from './pages/CourseRevenue/CourseRevenue';
import QuizResults from './pages/QuizResults/QuizResults';
import CourseAnalytics from './pages/CourseAnalytics/CourseAnalytics';
import StudentProgress from './pages/StudentProgress/StudentProgress';
import DropoffAnalysis from './pages/StudentProgress/DropoffAnalysis';
import CourseCertificates from './pages/CourseCertificates/CourseCertificates';
import CourseQA from './pages/CourseQA/CourseQA';
import TeacherLogin from './pages/Teachers/TeacherLogin';
import InviteTeacher from './pages/Teachers/InviteTeacher';
import AcceptInvitation from './pages/Teachers/AcceptInvitation';
import AssignStudents from './pages/Teachers/AssignStudents';
import TeacherActivity from './pages/Teachers/TeacherActivity';
import TeachersList from './pages/Teachers/TeachersList';
import TeacherDashboard from './pages/teacher/TeacherDashboard';
import MentorStudentDetail from './pages/mentor/MentorStudentDetail';
import StudentDetail from './pages/teacher/StudentDetail';
import StudentDetailPage from './pages/StudentDetail/StudentDetail';
import QuizFeedback from './pages/teacher/QuizFeedback';
import AtRiskDashboard from './pages/teacher/AtRiskDashboard';
import TeacherMetrics from './pages/teacher/TeacherMetrics';
import TeacherQA from './pages/teacher/TeacherQA';
import Statistics from './pages/Statistics/Statistics';
import CalendarPage from './pages/Calendar/Calendar';
import Promocodes from './pages/Promocodes/Promocodes';
import Leads from './pages/Leads/Leads';
import LeadDetail from './pages/Leads/LeadDetail';
import Reminders from './pages/Reminders/Reminders';
import CampaignDetail from './pages/Reminders/CampaignDetail';
import TeamHub from './pages/TeamHub/TeamHub';
import { I18nProvider } from './i18n/I18nContext';
import AppSplash from './components/AppSplash/AppSplash';
import AppToaster from './components/AppToaster/AppToaster';
import './App.css';

// B2B first slice — lazy-loaded so the main bundle isn't grown for a
// feature most tenants won't touch yet.
const ClientsHub = lazy(() => import('./pages/ClientsHub/ClientsHub'));
const ClientDetail = lazy(() => import('./pages/ClientDetail/ClientDetail'));

function LazyFallback() {
  return <AppSplash fullscreen={false} />;
}

// `/` opens the signed-in staff member's landing page (STAFF_ROLES[].landing), once the
// functional role is known.
function RoleLanding() {
  const { staffRole } = useAuth();
  if (!staffRole) return <AppSplash fullscreen={false} />;
  return <Navigate to={roleConfig(staffRole).landing} replace />;
}

// /courses/:courseId/lessons (and modules, quizzes) were standalone pages; they now live in the
// course workspace, so a saved link opens the same section there.
function WorkspaceSectionRedirect({ section }) {
  const { courseId } = useParams();
  return <Navigate to={`/courses/edit/${courseId}?tab=curriculum&c=${section}`} replace />;
}

function App() {
  return (
    <I18nProvider>
    <AppToaster />
    <Router>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/teacher-login" element={<TeacherLogin />} />
        <Route path="/invitations/accept" element={<AcceptInvitation />} />
        <Route path="/accept-invitation/:token" element={<AcceptInvitation />} />

        {/* Teacher-only routes — TeacherLayout with teacher sidebar */}
        <Route
          path="/teacher/*"
          element={
            <TeacherProtectedRoute>
              <TeacherLayout>
                <Routes>
                  <Route path="dashboard" element={<TeacherDashboard />} />
                  <Route path="students/:studentId" element={<StudentDetail />} />
                  <Route path="students/:studentId/assessment" element={<MentorStudentDetail />} />
                  <Route path="quiz-feedback/:attemptId" element={<QuizFeedback />} />
                  <Route path="at-risk" element={<AtRiskDashboard />} />
                  <Route path="metrics" element={<TeacherMetrics />} />
                  <Route path="qa" element={<TeacherQA />} />
                  <Route path="courses/:courseId/qa" element={<TeacherQA />} />
                  <Route path="*" element={<Navigate to="/teacher/dashboard" replace />} />
                </Routes>
              </TeacherLayout>
            </TeacherProtectedRoute>
          }
        />

        {/* Mentor/Admin routes — standard Layout, teachers redirected to /teacher/dashboard */}
        <Route
          path="/*"
          element={
            <MentorProtectedRoute>
              <Layout>
                <Routes>
                  <Route path="/" element={<RoleLanding />} />
                  <Route path="/dashboard" element={<Dashboard />} />

                  {/* Mentor Management */}
                  <Route path="/mentor/profile" element={<MentorProfile />} />
                  <Route path="/settings/mentor" element={<MentorProfile />} />
                  <Route path="/mentors" element={<MentorDirectory />} />
                  <Route path="/directory" element={<MentorDirectory />} />

                  {/* Mentor AI surfaces — Epic 19 + 20 (Phase D) */}
                  {/* Redesign 2026: the risk dashboard now lives as the "At-Risk" tab
                      inside the Students hub. Legacy routes redirect there. */}
                  <Route path="/mentor/students" element={<Navigate to="/students?tab=at-risk" replace />} />
                  <Route path="/mentor/students/:studentId" element={<MentorStudentDetail />} />
                  <Route path="/teacher-activity" element={<Navigate to="/students?tab=at-risk" replace />} />

                  {/* Course Management */}
                  <Route path="/courses" element={<Courses />} />
                  <Route path="/courses/create" element={<CourseCreation />} />
                  <Route path="/courses/edit/:id" element={<CourseOverview />} />
                  {/* The standalone section pages became workspace sections: old links land there. */}
                  <Route path="/courses/:courseId/modules" element={<WorkspaceSectionRedirect section="modules" />} />
                  <Route path="/courses/:courseId/lessons" element={<WorkspaceSectionRedirect section="lessons" />} />

                  {/* Enrollment Management */}
                  <Route path="/courses/:courseId/enrollments" element={<CourseEnrollments />} />

                  {/* Revenue & Payments */}
                  <Route path="/courses/:courseId/revenue" element={<CourseRevenue />} />
                  <Route path="/courses/:courseId/payments" element={<CourseRevenue />} />

                  {/* Quiz Management */}
                  <Route path="/courses/:courseId/quizzes" element={<WorkspaceSectionRedirect section="tests" />} />
                  <Route path="/courses/:courseId/quizzes/:quizId/results" element={<QuizResults />} />

                  {/* Analytics */}
                  <Route path="/courses/:courseId/analytics" element={<CourseAnalytics />} />
                  <Route path="/courses/:courseId/analytics/students" element={<StudentProgress />} />
                  <Route path="/courses/:courseId/students" element={<StudentProgress />} />
                  <Route path="/courses/:courseId/analytics/drop-off" element={<DropoffAnalysis />} />

                  {/* Certificates */}
                  <Route path="/courses/:courseId/certificates" element={<CourseCertificates />} />

                  {/* Q&A */}
                  <Route path="/courses/:courseId/qa" element={<CourseQA />} />

                  {/* Inbox hub — Messages + Feedback. Legacy routes redirect in. */}
                  <Route path="/inbox" element={<InboxHub />} />

                  {/* Support inbox — staff ticket management */}
                  <Route path="/support" element={<SupportHub />} />
                  <Route path="/messages" element={<Navigate to="/inbox" replace />} />
                  <Route path="/direct-messages" element={<Navigate to="/inbox" replace />} />
                  <Route path="/chat" element={<Navigate to="/inbox" replace />} />
                  <Route path="/feedback" element={<Navigate to="/inbox?tab=feedback" replace />} />

                  {/* Teacher Management (mentor manages teachers) */}
                  <Route path="/courses/:courseId/invite-teacher" element={<InviteTeacher />} />
                  <Route path="/courses/:courseId/teachers" element={<TeacherActivity />} />
                  <Route path="/courses/:courseId/teachers/list" element={<TeachersList />} />
                  <Route path="/courses/:courseId/teachers/:teacherId/assign" element={<AssignStudents />} />
                  <Route path="/courses/:courseId/assign-teachers" element={<InviteTeacher />} />

                  {/* Students hub — All Students + At-Risk */}
                  <Route path="/students" element={<StudentsHub />} />
                  <Route path="/students/:userId" element={<StudentDetailPage />} />

                  {/* Clients — B2B client organizations (first slice) */}
                  <Route
                    path="/clients"
                    element={
                      <Suspense fallback={<LazyFallback />}>
                        <ClientsHub />
                      </Suspense>
                    }
                  />
                  <Route
                    path="/clients/:clientId"
                    element={
                      <Suspense fallback={<LazyFallback />}>
                        <ClientDetail />
                      </Suspense>
                    }
                  />

                  {/* Sales hub — Orders + Discounts. Legacy routes redirect in. */}
                  <Route path="/sales" element={<SalesHub />} />

                  {/* Leads — the business's sales and call-centre worklist */}
                  <Route path="/leads" element={<Leads />} />
                  <Route path="/leads/:leadId" element={<LeadDetail />} />

                  {/* Reminders (Eslatmalar) — invitations and reminders to the business's own students */}
                  <Route path="/reminders" element={<Reminders />} />
                  <Route path="/reminders/:campaignId" element={<CampaignDetail />} />

                  {/* Promocodes — code-based discounts */}
                  <Route path="/promocodes" element={<Promocodes />} />
                  <Route path="/order-lists" element={<Navigate to="/sales" replace />} />
                  <Route path="/invoice" element={<Navigate to="/sales" replace />} />
                  <Route path="/discount" element={<Navigate to="/sales" replace />} />

                  {/* Admin */}
                  <Route path="/statistics" element={<Statistics />} />
                  <Route path="/team" element={<TeamHub />} />
                  <Route path="/teams" element={<Navigate to="/team?tab=teams" replace />} />
                  <Route path="/calendar" element={<CalendarPage />} />

                  {/* Coming Soon */}
                  <Route path="/offline-events" element={<ComingSoon titleKey="ui.offlineEventsTitle" descriptionKey="ui.offlineEventsDescription" />} />
                </Routes>
              </Layout>
            </MentorProtectedRoute>
          }
        />
      </Routes>
    </Router>
    </I18nProvider>
  );
}

export default App;
